import { isStaleAccountError } from '@/lib/stale-account';
import { useState, useEffect, useCallback, useRef } from "react";
import { 
  doc, 
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  onSnapshot, 
  serverTimestamp
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { useToast } from "@/hooks/use-toast";
import { debugLog } from "@/lib/error-monitoring";

export type FirebaseCartItem = {
  id: string;
  itemId: string;
  itemType: string;
  title: string;
  price: number;
  quantity: number;
  imageUrl?: string;
  details?: any;
  addedAt?: any;
};

export type FirebaseCart = {
  id: string;
  userId: string;
  items: FirebaseCartItem[];
  createdAt: any;
  updatedAt: any;
  total: number;
  itemCount: number;
};

export function useFirebaseCart() {
  const { user } = useFirebaseAuth();
  const { toast } = useToast();
  const [cart, setCart] = useState<FirebaseCart | null>(null);
  const [items, setItemsState] = useState<FirebaseCartItem[]>([]);
  // Every change builds on the latest list, not the one from the last
  // render: two quick adds used to each start from the same old list, so the
  // second overwrote the first (seen on production: only one of two saved).
  const itemsRef = useRef<FirebaseCartItem[]>([]);
  const setItems = useCallback((next: FirebaseCartItem[]) => {
    itemsRef.current = next;
    setItemsState(next);
  }, []);
  // Saves run one after another. A save made while another was in flight
  // used to be dropped (`if (isSyncing) return`) while the screen showed it.
  const writeChainRef = useRef<Promise<void>>(Promise.resolve());
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Get or create cart for authenticated user only
  const getOrCreateCart = useCallback(async () => {
    if (!user?.id) {
      console.log('[useFirebaseCart] No authenticated user, cannot create cart');
      return null;
    }

    try {
      // Query for existing cart
      const cartsRef = collection(db, 'carts');
      const q = query(cartsRef, where('userId', '==', user.id));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Return existing cart
        const cartDoc = querySnapshot.docs[0];
        const cartData = {
          id: cartDoc.id,
          ...cartDoc.data()
        } as FirebaseCart;
        
        return cartData;
      }

      // Create new cart
      const newCart = {
        userId: user.id,
        items: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        total: 0,
        itemCount: 0
      };

      const cartDocRef = await addDoc(cartsRef, newCart);
      const cartData = {
        id: cartDocRef.id,
        ...newCart,
        items: []
      } as FirebaseCart;
      
      console.log('🛒 Created new cart for authenticated user:', { userId: user.id, cartId: cartData.id });
      
      return cartData;
    } catch (error) {
      console.error("Error getting/creating cart:", error);
      return null;
    }
  }, [user?.id]);

  // Load cart and set up real-time listener
  useEffect(() => {
    if (!user?.id) {
      setCart(null);
      setItems([]);
      return;
    }

    setIsLoading(true);
    
    // Set up real-time listener for cart changes
    const cartsRef = collection(db, 'carts');
    const q = query(cartsRef, where('userId', '==', user.id));
    
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      if (snapshot.empty) {
        // Create cart if it doesn't exist
        const newCart = await getOrCreateCart();
        if (newCart) {
          setCart(newCart);
          setItems(newCart.items || []);
        }
      } else {
        // Use existing cart
        const cartDoc = snapshot.docs[0];
        const cartData = {
          id: cartDoc.id,
          ...cartDoc.data()
        } as FirebaseCart;
        
        setCart(cartData);
        setItems(cartData.items || []);
      }
      setIsLoading(false);
    }, (error) => {
      setIsLoading(false);
      // The account changed under the listener: not the user's problem.
      if (isStaleAccountError(error)) return;
      console.error("Error loading cart:", error);
      toast({
        title: "Error loading cart",
        description: "Failed to load your shopping cart",
        variant: "destructive"
      });
    });

    return () => unsubscribe();
  }, [user?.id, toast, getOrCreateCart]);

  // Update cart in Firestore
  const updateCart = useCallback((newItems: FirebaseCartItem[], cartIdOverride?: string) => {
    const targetCartId = cartIdOverride || cart?.id;
    if (!user?.id || !targetCartId) return Promise.resolve();
    const run = writeChainRef.current.then(() => writeCart(newItems, targetCartId));
    writeChainRef.current = run.catch(() => undefined);
    return run;
  }, [user?.id, cart?.id]);

  const writeCart = async (newItems: FirebaseCartItem[], targetCartId: string) => {

    setIsSyncing(true);
    debugLog('Syncing cart', { cartId: targetCartId, itemCount: newItems.length });

    try {
      // Clean items to remove undefined values
      const cleanedItems = newItems.map(item => {
        const cleanItem: Record<string, unknown> = {};
        Object.keys(item).forEach(key => {
          const value = (item as Record<string, unknown>)[key];
          if (value !== undefined && value !== null) {
            cleanItem[key] = value;
          }
        });
        return cleanItem;
      });

      // Calculate totals
      const total = cleanedItems.reduce((sum, item) => {
        const price = typeof item.price === 'string' ? parseFloat(item.price) || 0 : (item.price || 0);
        return sum + (price * (item.quantity || 1));
      }, 0);
      const itemCount = cleanedItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

      // Update cart document
      const cartRef = doc(db, 'carts', targetCartId);
      await updateDoc(cartRef, {
        items: cleanedItems,
        total,
        itemCount,
        updatedAt: serverTimestamp()
      });

      debugLog('Cart synced successfully', { cartId: targetCartId, itemCount: newItems.length });
    } catch (error) {
      console.error('❌ Error updating cart:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast({
        title: "Error updating cart",
        description: `Failed to update your cart: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // Add item to cart. Two layers of duplicate-add defense:
  //   1. `inFlightAddsRef` blocks concurrent calls for the same item while
  //      the previous add is still resolving — protects against the user
  //      double-clicking the "Add to cart" button before the first call
  //      writes to Firestore.
  //   2. The dedupe-by-itemId+itemType pass below already merges quantity
  //      when the same item exists, so even if two calls slip through the
  //      cart still ends up with one row.
  const inFlightAddsRef = useRef<Set<string>>(new Set());
  const addItem = useCallback(async (item: Omit<FirebaseCartItem, "id"> & { id?: string }) => {
    if (!user?.id) {
      toast({
        title: "Sign in required",
        description: "Please sign in to add items to your cart",
        variant: "destructive"
      });
      return;
    }

    const dedupeKey = `${item.itemType || 'resource'}::${String(item.itemId || item.id || '')}`;
    if (inFlightAddsRef.current.has(dedupeKey)) {
      return; // Quick second click while the first add is still pending — drop silently.
    }
    inFlightAddsRef.current.add(dedupeKey);

    try {
      const { assertSellerStorePublicForCartItem } = await import(
        "@/lib/commerce/seller-store-guard"
      );
      await assertSellerStorePublicForCartItem({
        itemType: item.itemType || "resource",
        itemId: String(item.itemId || item.id || ""),
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to add this item to your cart";
      toast({
        title: "Store unavailable",
        description: message,
        variant: "destructive",
      });
      inFlightAddsRef.current.delete(dedupeKey);
      return;
    }

    // Ensure cart exists
    let currentCart = cart;
    if (!currentCart) {
      currentCart = await getOrCreateCart();
      if (!currentCart) {
        toast({
          title: "Error",
          description: "Failed to create cart",
          variant: "destructive"
        });
        return;
      }
      setCart(currentCart);
    }

    const newItem: FirebaseCartItem = {
      id: item.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      itemId: item.itemId || item.id || '',
      itemType: item.itemType || 'resource',
      title: item.title || '',
      price: item.price || 0,
      quantity: item.quantity || 1,
      imageUrl: item.imageUrl || '',
      details: item.details || {},
      addedAt: new Date().toISOString()
    };

    // Check if item already exists
    const current = itemsRef.current;
    const existingIndex = current.findIndex(i => 
      i.itemId === newItem.itemId && i.itemType === newItem.itemType
    );
    
    let updatedItems: FirebaseCartItem[];
    if (existingIndex >= 0) {
      // Update quantity if item exists
      updatedItems = [...current];
      updatedItems[existingIndex] = {
        ...updatedItems[existingIndex],
        quantity: updatedItems[existingIndex].quantity + newItem.quantity
      };
    } else {
      // Add new item
      updatedItems = [...current, newItem];
    }

    setItems(updatedItems);
    try {
      await updateCart(updatedItems, currentCart.id);
      toast({
        title: "Added to cart",
        description: `${item.title} has been added to your cart`
      });
    } finally {
      inFlightAddsRef.current.delete(dedupeKey);
    }
  }, [user?.id, cart, updateCart, toast, getOrCreateCart, setItems]);

  // Remove item from cart
  const removeItem = useCallback(async (itemId: string) => {
    const updatedItems = itemsRef.current.filter(item => item.id !== itemId);
    setItems(updatedItems);
    await updateCart(updatedItems);
    
    toast({
      title: "Removed from cart",
      description: "Item has been removed from your cart"
    });
  }, [updateCart, toast, setItems]);

  // Remove several items in one write (removeItem per item would each filter
  // the same snapshot, so only the last removal would stick).
  const removeItems = useCallback(async (itemIds: string[]) => {
    const drop = new Set(itemIds);
    const updatedItems = itemsRef.current.filter(item => !drop.has(item.id));
    setItems(updatedItems);
    await updateCart(updatedItems);
  }, [updateCart, setItems]);

  // Update item quantity
  const updateQuantity = useCallback(async (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      await removeItem(itemId);
      return;
    }

    const updatedItems = itemsRef.current.map(item =>
      item.id === itemId ? { ...item, quantity } : item
    );
    setItems(updatedItems);
    await updateCart(updatedItems);
  }, [updateCart, removeItem, setItems]);

  // Clear entire cart
  const clearCart = useCallback(async () => {
    setItems([]);
    await updateCart([]);
    
    toast({
      title: "Cart cleared",
      description: "All items have been removed from your cart"
    });
  }, [updateCart, toast]);

  // Calculate totals
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const total = items.reduce((sum, item) => {
    const price = typeof item.price === 'string' ? parseFloat(item.price) : item.price;
    return sum + (price * item.quantity);
  }, 0);

  // Only log when values change, not on every render
  useEffect(() => {
    if (cart?.id && user?.id) {
      console.log('🛒 Firebase cart state', {
        cartId: cart?.id,
        userId: user.id,
        userType: 'authenticated',
        itemsLength: items.length,
        itemCount,
        total
      });
    }
  }, [cart?.id, items.length, itemCount, total, user?.id]);

  return {
    cart,
    items,
    addItem,
    removeItem,
    removeItems,
    updateQuantity,
    clearCart,
    itemCount,
    total,
    isLoading,
    isSyncing,
    currentUserId: user?.id || null,
    refetchCart: getOrCreateCart
  };
}
